/**
 * batch-68 gespreksvoorbereiding (FO-346) - pure feitenberekeningen.
 *
 * Geen database en geen klok: de API-loaders leveren de rijen aan, deze functies
 * tellen. Zo zijn de definities uit FO-346 §3 en §6 (weekend-telling,
 * nachtdienst, later begonnen/eerder gestopt, invuldeadline, kort verzuim,
 * melding na aanvang dienst, weekdagverdeling, korte-termijn-inzet,
 * flexibiliteit) los testbaar met grensgevallen (14/15/16 min, 7/8 dagen,
 * 47/48/49 uur).
 *
 * Feiten, geen oordelen: elke functie levert een telling, een duur of een
 * categorie van een tijdstip. Niets hier kwalificeert de medewerker.
 *
 * Datums zijn NL-kalenderdagen als 'yyyy-mm-dd' (Europe/Amsterdam); weken zijn
 * ISO-weken (maandag eerste dag).
 */

import {
  GV_INTERN_PREFIX,
  GV_MEDEWERKER_VERSIE_WEGLATEN,
  GV_MEDEWERKER_ZINNEN,
  GV_VERZUIM_VERDELING_KEYS,
  type GespreksvoorbereidingDTO,
  type GvBlok,
  type GvCel,
  type GvDetailLijst,
  type GvKerncijfer,
  type GvPeriodeKeuze,
  type GvRubriek,
  type GvRubriekKey,
  type GvWaarde,
} from '@streep/types'
import { ymdNaarNlDatum, ymdPlusDagen } from './gespreksverslag-deadline'
import { parseHHmmToMinutes } from './werkbon-hours'

const MS_PER_DAG = 24 * 60 * 60 * 1000

function pad2(n: number): string {
  return String(n).padStart(2, '0')
}

// ─── Datums en periode ───────────────────────────────────────────────────────

/** `ymd` plus `maanden` kalendermaanden; een te korte maand klemt op de laatste dag. */
export function gvYmdPlusMaanden(ymd: string, maanden: number): string {
  const [jaar, maand, dag] = ymd.split('-').map(Number)
  const doel = new Date(Date.UTC(jaar, maand - 1 + maanden, 1))
  const laatste = new Date(Date.UTC(doel.getUTCFullYear(), doel.getUTCMonth() + 1, 0)).getUTCDate()
  return `${doel.getUTCFullYear()}-${pad2(doel.getUTCMonth() + 1)}-${pad2(Math.min(dag, laatste))}`
}

/** Aantal kalenderdagen van `van` tot en met `tot`. */
export function gvDagenInclusief(van: string, tot: string): number {
  return Math.round((Date.parse(`${tot}T00:00:00Z`) - Date.parse(`${van}T00:00:00Z`)) / MS_PER_DAG) + 1
}

/** ISO-weekdag van een kalenderdag: 1 = maandag ... 7 = zondag. */
export function gvWeekdag(ymd: string): number {
  const dag = new Date(`${ymd}T12:00:00Z`).getUTCDay()
  return dag === 0 ? 7 : dag
}

/** ISO-week als { jaar, week } van een kalenderdag. */
export function gvIsoWeek(ymd: string): { jaar: number; week: number } {
  const d = new Date(`${ymd}T12:00:00Z`)
  const dag = d.getUTCDay() || 7
  d.setUTCDate(d.getUTCDate() + 4 - dag)
  const jaar = d.getUTCFullYear()
  const jan1 = Date.UTC(jaar, 0, 1)
  const week = Math.ceil(((d.getTime() - jan1) / MS_PER_DAG + 1) / 7)
  return { jaar, week }
}

/** Sleutel per ISO-week ("2026-W07"), sorteerbaar. */
export function gvIsoWeekSleutel(ymd: string): string {
  const { jaar, week } = gvIsoWeek(ymd)
  return `${jaar}-W${pad2(week)}`
}

/** De maandag van de ISO-week van `ymd`. */
export function gvMaandagVan(ymd: string): string {
  return ymdPlusDagen(ymd, 1 - gvWeekdag(ymd))
}

export interface GvPeriodeInput {
  keuze: GvPeriodeKeuze
  /** Vandaag als NL-kalenderdag. */
  vandaag: string
  /** Gespreksdatum (bij een gepland gesprek): de periode loopt tot die dag. */
  gesprekDatum: string | null
  /** Datum van het vorige afgeronde gesprek (van dit type) vóór de einddatum. */
  vorigGesprekDatum: string | null
  vrijVan: string | null
  vrijTot: string | null
  inDienstSinds: string | null
  standaardMaanden: number
  /** Langste periode in maanden (laadtijd); een langere keuze wordt afgekapt, met melding. */
  maxMaanden?: number
}

export interface GvPeriodeResultaat {
  van: string
  tot: string
  dagen: number
  meldingen: string[]
  /** De eigen vorige periode van gelijke lengte; null = de vergelijking vervalt. */
  vorige: { van: string; tot: string } | null
}

function maandenTerug(tot: string, maanden: number): string {
  return ymdPlusDagen(gvYmdPlusMaanden(tot, -maanden), 1)
}

/**
 * Bepaal de periode (FO-346 §3.1 en §6). Standaard tot vandaag, of tot de
 * gespreksdatum. Korter in dienst dan de periode: de periode begint bij de
 * indiensttreding en de vergelijking vervalt (nooit een stille vergelijking met
 * een periode waarin hij nog niet in dienst was).
 */
export function bepaalGvPeriode(input: GvPeriodeInput): GvPeriodeResultaat {
  const tot = input.keuze === 'VRIJ' && input.vrijTot ? input.vrijTot : (input.gesprekDatum ?? input.vandaag)
  const meldingen: string[] = []
  let van: string
  switch (input.keuze) {
    case '3M':
      van = maandenTerug(tot, 3)
      break
    case '6M':
      van = maandenTerug(tot, 6)
      break
    case '12M':
      van = maandenTerug(tot, 12)
      break
    case 'VRIJ':
      van = input.vrijVan ?? maandenTerug(tot, input.standaardMaanden)
      break
    case 'SINDS_VORIG_GESPREK':
      if (input.vorigGesprekDatum && input.vorigGesprekDatum <= tot) {
        van = input.vorigGesprekDatum
      } else {
        van = maandenTerug(tot, input.standaardMaanden)
        meldingen.push(
          `Geen eerder afgerond gesprek gevonden; de standaardperiode van ${input.standaardMaanden} maanden is gebruikt.`,
        )
      }
      break
  }

  if (input.maxMaanden != null) {
    const grens = maandenTerug(tot, input.maxMaanden)
    if (van < grens) {
      van = grens
      meldingen.push(`De periode is beperkt tot de laatste ${input.maxMaanden} maanden.`)
    }
  }

  let vergelijkingMogelijk = true
  if (input.inDienstSinds && input.inDienstSinds > van) {
    van = input.inDienstSinds > tot ? tot : input.inDienstSinds
    meldingen.push(
      `In dienst sinds ${ymdNaarNlDatum(input.inDienstSinds)}; de periode begint bij de indiensttreding. De vergelijking met een vorige periode vervalt.`,
    )
    vergelijkingMogelijk = false
  }

  const dagen = gvDagenInclusief(van, tot)
  let vorige: GvPeriodeResultaat['vorige'] = null
  if (vergelijkingMogelijk) {
    const vorigeTot = ymdPlusDagen(van, -1)
    const vorigeVan = ymdPlusDagen(vorigeTot, -(dagen - 1))
    if (input.inDienstSinds && vorigeVan < input.inDienstSinds) {
      meldingen.push('De vorige periode van gelijke lengte valt deels voor de indiensttreding; de vergelijking vervalt.')
    } else {
      vorige = { van: vorigeVan, tot: vorigeTot }
    }
  }
  return { van, tot, dagen, meldingen, vorige }
}

/** De laatste 12 maanden tot en met `tot` (verzuimfrequentie, FO-346 §3.5). */
export function gvTwaalfMaandenTot(tot: string): { van: string; tot: string } {
  return { van: maandenTerug(tot, 12), tot }
}

/** True als `ymd` binnen [van, tot] valt (beide inclusief). */
export function gvInPeriode(ymd: string, periode: { van: string; tot: string }): boolean {
  return ymd >= periode.van && ymd <= periode.tot
}

// ─── Tijden ──────────────────────────────────────────────────────────────────

/**
 * Verschil in minuten (werkelijk minus gepland) tussen twee klokstanden,
 * genormaliseerd rond middernacht: gepland 23:00 en werkelijk 00:30 is +90.
 * null bij een ongeldige tijd.
 */
export function gvKlokVerschilMinuten(gepland: string, werkelijk: string): number | null {
  const g = parseHHmmToMinutes(gepland)
  const w = parseHHmmToMinutes(werkelijk)
  if (g == null || w == null) return null
  let d = w - g
  if (d > 720) d -= 1440
  if (d <= -720) d += 1440
  return d
}

/** Later begonnen dan gepland: meer dan `drempelMinuten` na de geplande begintijd. */
export function gvLaterBegonnen(
  plannedStart: string,
  actualStart: string | null,
  drempelMinuten: number,
): { ja: boolean; minuten: number | null } {
  if (!actualStart) return { ja: false, minuten: null }
  const d = gvKlokVerschilMinuten(plannedStart, actualStart)
  if (d == null) return { ja: false, minuten: null }
  return { ja: d > drempelMinuten, minuten: d }
}

/** Eerder gestopt dan gepland: meer dan `drempelMinuten` voor de geplande eindtijd. */
export function gvEerderGestopt(
  plannedEnd: string,
  actualEnd: string | null,
  drempelMinuten: number,
): { ja: boolean; minuten: number | null } {
  if (!actualEnd) return { ja: false, minuten: null }
  const d = gvKlokVerschilMinuten(plannedEnd, actualEnd)
  if (d == null) return { ja: false, minuten: null }
  return { ja: d < -drempelMinuten, minuten: -d }
}

/** Aandeel (0..1) van de dienst [start, eind) tussen 22:00 en 06:00; over middernacht mag. */
export function gvNachtAandeel(start: string, eind: string): number | null {
  const s = parseHHmmToMinutes(start)
  const e0 = parseHHmmToMinutes(eind)
  if (s == null || e0 == null) return null
  let e = e0
  if (e <= s) e += 1440
  const duur = e - s
  if (duur <= 0) return null
  const nachtVensters: Array<[number, number]> = [
    [0, 360],
    [1320, 1800],
    [2760, 2880],
  ]
  let overlap = 0
  for (const [a, b] of nachtVensters) overlap += Math.max(0, Math.min(e, b) - Math.max(s, a))
  return overlap / duur
}

/** Nachtdienst: ploegsoort NACHT, of meer dan `drempelProcent` van de uren tussen 22:00 en 06:00. */
export function gvIsNachtdienst(input: {
  ploegSoort: string | null
  start: string | null
  eind: string | null
  drempelProcent: number
}): boolean {
  if (input.ploegSoort === 'NACHT') return true
  if (!input.start || !input.eind) return false
  const aandeel = gvNachtAandeel(input.start, input.eind)
  return aandeel != null && aandeel * 100 > input.drempelProcent
}

/** Weekenden met werk (za en zo samen tellen als één weekend), plus losse za/zo-tellingen. */
export function gvTelWeekenden(werkdagen: Iterable<string>): {
  weekenden: number
  zaterdagen: number
  zondagen: number
} {
  const dagen = new Set(werkdagen)
  let zaterdagen = 0
  let zondagen = 0
  const weekendSleutels = new Set<string>()
  for (const d of dagen) {
    const wd = gvWeekdag(d)
    if (wd === 6) {
      zaterdagen++
      weekendSleutels.add(d)
    } else if (wd === 7) {
      zondagen++
      weekendSleutels.add(ymdPlusDagen(d, -1))
    }
  }
  return { weekenden: weekendSleutels.size, zaterdagen, zondagen }
}

// ─── Invullen van de bon ─────────────────────────────────────────────────────

/** Werkdag voor de invuldeadline: maandag t/m vrijdag, geen verplichte feestdag. */
export function gvIsWerkdag(ymd: string, feestdagen: ReadonlySet<string>): boolean {
  const wd = gvWeekdag(ymd)
  return wd <= 5 && !feestdagen.has(ymd)
}

/**
 * Laatste dag (tot en met) waarop werkdag `ymd` op tijd is ingevuld: het einde
 * van de `werkdagenNa`-de werkdag erna (0 = de werkdag zelf).
 */
export function gvBonDeadlineDag(ymd: string, werkdagenNa: number, isWerkdag: (ymd: string) => boolean): string {
  let d = ymd
  let n = 0
  let stappen = 0
  while (n < werkdagenNa && stappen < 60) {
    d = ymdPlusDagen(d, 1)
    stappen++
    if (isWerkdag(d)) n++
  }
  return d
}

export type GvInvulUitkomst = 'OP_TIJD' | 'NA_DEADLINE' | 'NIET_VAST_TE_STELLEN'

/**
 * "Bon te laat ingevuld" op een BENADERING (FO-346 §3.4, ADDENDA punt 4).
 * STREEP legt het eerste invoermoment van de werkelijke tijden niet vast; het
 * laatste wijzigingsmoment van de dagregel is een bovengrens. Ligt dat vóór de
 * deadline, dan was de dag zeker op tijd. Ligt het erna, dan is het "na de
 * deadline (benadering)" - behalve als een ander dan de medewerker (kantoor,
 * klant of systeem: een goedkeuring, afkeuring, heropening, klantweigering,
 * pauzeherrekening of urenwijziging) tussen de deadline en die laatste wijziging
 * aan de bon zat. Dan kan die laatste wijziging van die ander zijn, of een
 * reactie erop: het invulmoment is onbekend en de dag telt niet mee
 * (NIET_VAST_TE_STELLEN). Een moment van een ander vóór de deadline, of ruim
 * na de laatste wijziging, verklaart de late wijziging niet.
 */
export function gvInvulUitkomst(input: {
  laatsteWijziging: Date
  deadlineEinde: Date
  /** Momenten waarop een ander dan de medewerker de bon of deze dagregel wijzigde of beoordeelde. */
  vreemdeMomenten: ReadonlyArray<Date | null>
}): GvInvulUitkomst {
  const d = input.deadlineEinde.getTime()
  const t = input.laatsteWijziging.getTime()
  if (t <= d) return 'OP_TIJD'
  const verklaard = input.vreemdeMomenten.some((m) => m != null && m.getTime() > d && m.getTime() <= t + 5000)
  return verklaard ? 'NIET_VAST_TE_STELLEN' : 'NA_DEADLINE'
}

// ─── Verzuim ─────────────────────────────────────────────────────────────────

/** Kort verzuim: afgesloten en hooguit `drempelDagen` kalenderdagen. null = loopt nog. */
export function gvIsKortVerzuim(start: string, eind: string | null, drempelDagen: number): boolean | null {
  if (eind == null) return null
  return gvDagenInclusief(start, eind) <= drempelDagen
}

/** Verdeling van de eerste ziektedag per weekdag: index 0 = maandag ... 6 = zondag. Alleen tellingen. */
export function gvVerdelingEersteZiektedag(startDagen: Iterable<string>): number[] {
  const telling = [0, 0, 0, 0, 0, 0, 0]
  for (const d of startDagen) telling[gvWeekdag(d) - 1]++
  return telling
}

/**
 * Sluit de melding direct aan op een vrije dag (weekend, feestdag, roostervrije
 * dag of verlof)? Kijkt naar de dag vóór de eerste ziektedag en de dag na de
 * laatste. Een telling, geen label.
 */
export function gvSluitAanOpVrijeDag(start: string, eind: string | null, isVrij: (ymd: string) => boolean): boolean {
  if (isVrij(ymdPlusDagen(start, -1))) return true
  return eind != null && isVrij(ymdPlusDagen(eind, 1))
}

export type GvMeldingMoment =
  | { soort: 'NA_AANVANG'; minuten: number }
  | { soort: 'VOOR_AANVANG'; minuten: number }
  | { soort: 'GEEN_GEPLANDE_DIENST' }
  | { soort: 'NIET_VAST_TE_STELLEN'; reden: string }

/**
 * Lag de ziekmelding na de geplande begintijd van die dag? Benaming in de UI:
 * "melding na aanvang geplande dienst", nooit "te laat".
 */
export function gvMeldingMoment(input: {
  meldingOp: Date | null
  geplandeAanvang: Date | null
  nietVastReden: string | null
}): GvMeldingMoment {
  if (input.nietVastReden) return { soort: 'NIET_VAST_TE_STELLEN', reden: input.nietVastReden }
  if (!input.meldingOp) return { soort: 'NIET_VAST_TE_STELLEN', reden: 'Geen meldtijdstip vastgelegd' }
  if (!input.geplandeAanvang) return { soort: 'GEEN_GEPLANDE_DIENST' }
  const minuten = Math.round((input.meldingOp.getTime() - input.geplandeAanvang.getTime()) / 60000)
  return minuten > 0 ? { soort: 'NA_AANVANG', minuten } : { soort: 'VOOR_AANVANG', minuten: -minuten }
}

// ─── Inzet en positieve signalen ─────────────────────────────────────────────

/** Toegewezen hooguit `drempelUren` voor aanvang (en niet erna). */
export function gvIsKorteTermijn(toegewezenOp: Date, aanvang: Date, drempelUren: number): boolean {
  const verschil = aanvang.getTime() - toegewezenOp.getTime()
  return verschil >= 0 && verschil <= drempelUren * 60 * 60 * 1000
}

export interface GvInzetDag {
  datum: string
  clientId: string
  dienstSoort: string | null
}

/**
 * Flexibiliteit (FO-346 §3.10): dagen bij een andere klant, en dagen in een
 * andere ploeg/dienstsoort, dan de meest voorkomende in de periode. Telt
 * kalenderdagen; gelijke stand beslist deterministisch op de sleutel.
 */
export function gvTelFlexibiliteit(dagen: GvInzetDag[]): {
  meestVoorkomendeKlantId: string | null
  dagenAndereKlant: number
  meestVoorkomendeDienst: string | null
  dagenAndereDienst: number
} {
  const klantDagen = new Map<string, Set<string>>()
  const dienstDagen = new Map<string, Set<string>>()
  for (const d of dagen) {
    const k = klantDagen.get(d.clientId) ?? new Set<string>()
    k.add(d.datum)
    klantDagen.set(d.clientId, k)
    if (d.dienstSoort) {
      const s = dienstDagen.get(d.dienstSoort) ?? new Set<string>()
      s.add(d.datum)
      dienstDagen.set(d.dienstSoort, s)
    }
  }
  const meest = (m: Map<string, Set<string>>): string | null => {
    let beste: string | null = null
    let aantal = -1
    for (const [sleutel, set] of [...m.entries()].sort((a, b) => a[0].localeCompare(b[0]))) {
      if (set.size > aantal) {
        beste = sleutel
        aantal = set.size
      }
    }
    return beste
  }
  const vasteKlant = meest(klantDagen)
  const vasteDienst = meest(dienstDagen)
  const andereKlant = new Set<string>()
  const andereDienst = new Set<string>()
  for (const d of dagen) {
    if (vasteKlant && d.clientId !== vasteKlant) andereKlant.add(d.datum)
    if (vasteDienst && d.dienstSoort && d.dienstSoort !== vasteDienst) andereDienst.add(d.datum)
  }
  return {
    meestVoorkomendeKlantId: vasteKlant,
    dagenAndereKlant: andereKlant.size,
    meestVoorkomendeDienst: vasteDienst,
    dagenAndereDienst: andereDienst.size,
  }
}

/** Aantal weken met gewerkte uren boven de contracturen van die week (alleen de telling). */
export function gvTelWekenBovenContract(weken: ReadonlyArray<{ uren: number; contractUren: number | null }>): number {
  return weken.filter((w) => w.contractUren != null && w.contractUren > 0 && w.uren > w.contractUren + 0.005).length
}

// ─── Checklist-antwoorden (PBM-inspectie) ────────────────────────────────────

const AFWIJKENDE_KEUZES = new Set(['slecht', 'ontbreekt', 'ontbrekend', 'defect', 'afgekeurd', 'niet aanwezig'])
/** Vragen waarop "Ja" juist het probleem meldt (zoals de PBM-seed "X vervangen nodig"). */
const JA_MELDT_AFWIJKING = /(vervang(en|ing) nodig|schade aanwezig|beschadigd|defect|ontbreekt)/i

/**
 * Is dit antwoord afwijkend (FO-346 §3.6: "nee/slecht/ontbreekt")? Afwijkend =
 * "Nee" op een vraag naar iets dat in orde moet zijn (of een vraag die als risico
 * bij Nee is ingesteld), "Ja" op een vraag als "vervangen nodig" of "schade
 * aanwezig", of een keuze "Slecht"/"Ontbreekt". "N.v.t." en tekstantwoorden niet.
 */
export function gvIsAfwijkendAntwoord(input: {
  risicoBijNee: boolean
  vraag: string
  antwoordBool: boolean | null
  antwoordKeuze: string | null
}): boolean {
  const keuze = input.antwoordKeuze?.trim().toLowerCase()
  if (keuze && AFWIJKENDE_KEUZES.has(keuze)) return true
  const jaMeldtProbleem = JA_MELDT_AFWIJKING.test(input.vraag)
  if (input.antwoordBool === false) return input.risicoBijNee || !jaMeldtProbleem
  if (input.antwoordBool === true) return jaMeldtProbleem && !input.risicoBijNee
  return false
}

// ─── Vergelijking en weergave ────────────────────────────────────────────────

/** Getal achter een waarde (voor de vergelijking); null bij tekst. */
export function gvWaardeGetal(w: GvWaarde): number | null {
  switch (w.soort) {
    case 'AANTAL':
      return w.aantal
    case 'UREN':
      return w.uren
    case 'DAGEN':
      return w.dagen
    case 'MINUTEN':
      return w.minuten
    case 'KM':
      return w.km
    case 'X_VAN_Y':
      return w.x
    case 'TEKST':
      return null
  }
}

/** Neutrale richting t.o.v. de vorige periode (op 2 decimalen). */
export function gvRichting(nu: number, vorige: number): 'HOGER' | 'LAGER' | 'GELIJK' {
  const d = Math.round((nu - vorige) * 100) / 100
  if (d > 0) return 'HOGER'
  if (d < 0) return 'LAGER'
  return 'GELIJK'
}

export type GvUrenWeergave = 'DECIMAAL' | 'UMM'

const NL_2DEC = new Intl.NumberFormat('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const NL_GETAL = new Intl.NumberFormat('nl-NL', { maximumFractionDigits: 1 })

/** Uren met twee decimalen ("41,50") of als u:mm ("41:30"). */
export function formatGvUren(uren: number, weergave: GvUrenWeergave = 'DECIMAAL'): string {
  if (weergave === 'UMM') {
    const negatief = uren < 0
    const totaal = Math.round(Math.abs(uren) * 60)
    return `${negatief ? '-' : ''}${Math.floor(totaal / 60)}:${pad2(totaal % 60)}`
  }
  return NL_2DEC.format(uren)
}

export function formatGvWaarde(w: GvWaarde, weergave: GvUrenWeergave = 'DECIMAAL'): string {
  switch (w.soort) {
    case 'AANTAL':
      return NL_GETAL.format(w.aantal)
    case 'UREN':
      return `${formatGvUren(w.uren, weergave)} u`
    case 'DAGEN':
      return `${NL_GETAL.format(w.dagen)} ${w.dagen === 1 ? 'dag' : 'dagen'}`
    case 'MINUTEN':
      return `${NL_GETAL.format(w.minuten)} min`
    case 'KM':
      return `${NL_GETAL.format(w.km)} km`
    case 'X_VAN_Y':
      return `${NL_GETAL.format(w.x)} van ${NL_GETAL.format(w.y)}`
    case 'TEKST':
      return w.tekst
  }
}

export function formatGvCel(c: GvCel, weergave: GvUrenWeergave = 'DECIMAAL'): string {
  switch (c.t) {
    case 'TEKST':
      return c.v
    case 'DATUM':
      return ymdNaarNlDatum(c.v.slice(0, 10))
    case 'DATUMTIJD': {
      const d = new Date(c.v)
      if (Number.isNaN(d.getTime())) return c.v
      return new Intl.DateTimeFormat('nl-NL', {
        timeZone: 'Europe/Amsterdam',
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      }).format(d)
    }
    case 'UREN':
      return formatGvUren(c.v, weergave)
    case 'AANTAL':
      return NL_GETAL.format(c.v)
    case 'MINUTEN':
      return `${NL_GETAL.format(c.v)} min`
    case 'LEEG':
      return '-'
  }
}

// ─── Versie voor de medewerker ───────────────────────────────────────────────

/** De verzuimverdeling (eerste ziektedag per weekdag, aansluitend op een vrije dag): alleen in de verzuimrubriek. */
export function gvIsVerzuimVerdeling(key: string, rubriek?: GvRubriekKey): boolean {
  return rubriek === 'VERZUIM' && (GV_VERZUIM_VERDELING_KEYS as readonly string[]).includes(key)
}

/**
 * Gaat dit onderdeel nooit naar de medewerker? De enige definitie voor het weglaten in
 * de medewerkerversie: de prefix `GV_INTERN_PREFIX`, een sleutel uit
 * `GV_MEDEWERKER_VERSIE_WEGLATEN` (in elke rubriek), en in de verzuimrubriek de
 * verzuimverdeling (`GV_VERZUIM_VERDELING_KEYS`). Of paneel en PDF er een label bij
 * zetten, bepaalt `gvToonAlleenInternLabel`.
 */
export function gvIsAlleenIntern(key: string, rubriek?: GvRubriekKey): boolean {
  if (key.startsWith(GV_INTERN_PREFIX) || (GV_MEDEWERKER_VERSIE_WEGLATEN as readonly string[]).includes(key)) return true
  return gvIsVerzuimVerdeling(key, rubriek)
}

/**
 * Draagt dit onderdeel in paneel en PDF het label "Alleen intern" (met slot en eigen
 * kader)? Elk intern onderdeel, behalve de verzuimverdeling: die is een kale telling,
 * zonder label, kleur of norm (FO-346 §3.5). Dat de verdeling niet in het
 * feitenoverzicht voor de medewerker staat, zegt één neutrale zin op rubriekniveau
 * (`gvToontVerzuimVerdeling` met `GV_ZIN_VERDELING_NIET_IN_FEITENOVERZICHT`).
 */
export function gvToonAlleenInternLabel(key: string, rubriek?: GvRubriekKey): boolean {
  return gvIsAlleenIntern(key, rubriek) && !gvIsVerzuimVerdeling(key, rubriek)
}

/**
 * Toont deze rubriek een onderdeel van de verzuimverdeling (kerncijfer, blok of
 * detaillijst)? Dan hoort de neutrale rubriekzin erbij. Een lege rubriek toont de
 * onderdelen niet, en in de versie voor de medewerker is de verdeling weggelaten: daar
 * dus nooit.
 */
export function gvToontVerzuimVerdeling(r: GvRubriek): boolean {
  if (!r.beschikbaar || r.leeg) return false
  const verdeling = (key: string) => gvIsVerzuimVerdeling(key, r.key)
  return r.kerncijfers.some((k) => verdeling(k.key)) || r.blokken.some((b) => verdeling(b.key)) || r.details.some((d) => verdeling(d.key))
}

/** Een tekstwaarde zonder gegeven: leeg, "-", "geen" of alleen nullen ("0 / 0 / 0"). */
function gvIsLegeTekst(tekst: string): boolean {
  const t = tekst.trim().toLowerCase()
  return t === 'geen' || /^[\s0/-]*$/.test(t)
}

/** Staat er na het weglaten nog een gegeven in de rubriek (een getal boven nul, een regel, een notitie)? */
function gvHeeftInhoud(kerncijfers: GvKerncijfer[], blokken: GvBlok[], details: GvDetailLijst[]): boolean {
  const kerncijferMetWaarde = kerncijfers.some((k) => {
    if (k.waarde.soort === 'TEKST') return !gvIsLegeTekst(k.waarde.tekst)
    return (gvWaardeGetal(k.waarde) ?? 0) > 0
  })
  const blokMetInhoud = blokken.some((b) => {
    switch (b.soort) {
      case 'LIJST':
        return b.rijen.length > 0
      case 'NOTITIES':
        return b.notities.length > 0
      case 'STAAFJES':
        return b.staven.some((s) => s.waarde > 0)
      case 'TEKST':
        return b.tekst.trim() !== ''
      case 'NIET_BESCHIKBAAR':
        return false
    }
  })
  return kerncijferMetWaarde || blokMetInhoud || details.some((d) => d.rijen.length > 0)
}

/** Een zin over de rechten van de lezer gaat in de medewerkerversie over de maker: neutraal maken. */
function gvMedewerkerTekst(tekst: string): string {
  let uit = tekst
  for (const [kijker, medewerker] of GV_MEDEWERKER_ZINNEN) uit = uit.split(kijker).join(medewerker)
  return uit
}

/**
 * De bijlage voor de medewerker (FO-346 §3.11): zonder de interne onderdelen
 * (vertrouwelijke HR-notities, de verzuimverdeling, interne doelen, niet-gedeelde
 * en klantbeoordelingen, interne actiepunten en die uit nog niet vrijgegeven
 * verslagen: zie
 * `GV_MEDEWERKER_VERSIE_WEGLATEN` en `GV_INTERN_PREFIX`). Laat de rest ongemoeid en
 * zet alle acties uit. Blijft er in een rubriek na het weglaten niets over, dan is
 * die leeg, net als zonder die gegevens: anders verraadt de rubriek dat er iets is
 * weggelaten.
 */
export function maakGvMedewerkerVersie(dto: GespreksvoorbereidingDTO): GespreksvoorbereidingDTO {
  const rubrieken: GvRubriek[] = dto.rubrieken.map((r) => {
    if (!r.beschikbaar) return r
    const weg = (key: string) => gvIsAlleenIntern(key, r.key)
    const kerncijfers = r.kerncijfers
      .filter((k) => !weg(k.key))
      .map((k) => ({ ...k, uitleg: gvMedewerkerTekst(k.uitleg), sub: k.sub == null ? null : gvMedewerkerTekst(k.sub) }))
    const blokken = r.blokken
      .filter((b) => !weg(b.key) && !(b.soort === 'NOTITIES' && b.vertrouwelijk))
      .map((b) => ((b.soort === 'LIJST' || b.soort === 'STAAFJES') && b.uitleg ? { ...b, uitleg: gvMedewerkerTekst(b.uitleg) } : b))
    const details = r.details
      .filter((d) => !weg(d.key))
      .map((d) => (d.toelichting ? { ...d, toelichting: gvMedewerkerTekst(d.toelichting) } : d))
    const ingekort = kerncijfers.length < r.kerncijfers.length || blokken.length < r.blokken.length || details.length < r.details.length
    return {
      ...r,
      leeg: r.leeg || (ingekort && !gvHeeftInhoud(kerncijfers, blokken, details)),
      kerncijfers,
      blokken,
      details,
      toelichtingen: r.toelichtingen.map(gvMedewerkerTekst),
    }
  })
  return {
    ...dto,
    rubrieken,
    medewerkerVersie: true,
    rechten: { snapshotVastleggen: false, instellingenBeheren: false },
  }
}
